import {
  CATEGORIES,
  etbToSantim,
  santimToEtb,
  type AdminMarketDto,
  type AppConfigDto,
  type Category,
} from '@bravo/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from '../components/ConfirmDialog';
import {
  Badge,
  Button,
  Card,
  ErrorBlock,
  Field,
  PageHeader,
  Select,
  Spinner,
  TextArea,
  TextInput,
  statusTone,
} from '../components/ui';
import { apiData } from '../lib/api';
import { toastError, toastOk } from '../lib/feedback';
import { useFormat } from '../lib/format';
import { addisDaysFromNow, fromAddisInput, toAddisInput } from '../lib/time';

interface OutcomeDraft {
  key: string;
  en: string;
  am: string;
}

interface Draft {
  titleEn: string;
  titleAm: string;
  descEn: string;
  descAm: string;
  category: Category;
  closesAt: string;
  resolutionDate: string;
  sourceEn: string;
  sourceAm: string;
  sourceUrl: string;
  outcomes: OutcomeDraft[];
  featured: boolean;
  imageUrl: string;
  feeBps: string;
  minStake: string;
  maxStake: string;
}

const empty = (): Draft => ({
  titleEn: '',
  titleAm: '',
  descEn: '',
  descAm: '',
  category: 'economy',
  closesAt: addisDaysFromNow(2),
  resolutionDate: addisDaysFromNow(3),
  sourceEn: '',
  sourceAm: '',
  sourceUrl: '',
  outcomes: [
    { key: 'yes', en: 'Yes', am: 'አዎ' },
    { key: 'no', en: 'No', am: 'አይ' },
  ],
  featured: false,
  imageUrl: '',
  feeBps: '',
  minStake: '',
  maxStake: '',
});

function fromMarket(market: AdminMarketDto): Draft {
  return {
    titleEn: market.title.en,
    titleAm: market.title.am,
    descEn: market.description.en,
    descAm: market.description.am,
    category: market.category,
    closesAt: toAddisInput(market.closesAt),
    resolutionDate: toAddisInput(market.resolutionDate),
    sourceEn: market.resolutionSource.en,
    sourceAm: market.resolutionSource.am,
    sourceUrl: market.resolutionSource.url ?? '',
    outcomes: market.outcomes.map((outcome) => ({
      key: outcome.key,
      en: outcome.title.en,
      am: outcome.title.am,
    })),
    featured: market.featured,
    imageUrl: market.imageUrl ?? '',
    feeBps: String(market.feeBps),
    minStake: santimToEtb(market.minStake),
    maxStake: santimToEtb(market.maxStake),
  };
}

function payload(draft: Draft, includeLimits: boolean) {
  const sourceUrl = draft.sourceUrl.trim();
  const imageUrl = draft.imageUrl.trim();
  return {
    title: { en: draft.titleEn, am: draft.titleAm },
    description: { en: draft.descEn, am: draft.descAm },
    category: draft.category,
    closesAt: fromAddisInput(draft.closesAt),
    resolutionDate: fromAddisInput(draft.resolutionDate),
    resolutionSource: {
      en: draft.sourceEn,
      am: draft.sourceAm,
      ...(sourceUrl ? { url: sourceUrl } : {}),
    },
    outcomes: draft.outcomes.map((outcome) => ({
      key: outcome.key,
      title: { en: outcome.en, am: outcome.am },
    })),
    featured: draft.featured,
    ...(imageUrl ? { imageUrl } : {}),
    ...(includeLimits && draft.feeBps.trim() ? { feeBps: Number(draft.feeBps) } : {}),
    ...(includeLimits && draft.minStake.trim() ? { minStake: etbToSantim(draft.minStake) } : {}),
    ...(includeLimits && draft.maxStake.trim() ? { maxStake: etbToSantim(draft.maxStake) } : {}),
  };
}

export function MarketEditPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const { t } = useTranslation();
  const format = useFormat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(empty);
  const [busy, setBusy] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [winner, setWinner] = useState('yes');

  const market = useQuery({
    queryKey: ['market', id],
    enabled: !isNew,
    queryFn: () => apiData<AdminMarketDto>(`/admin/markets/${id}`),
  });
  const config = useQuery({
    queryKey: ['config'],
    queryFn: () => apiData<AppConfigDto>('/admin/config'),
    retry: false,
  });

  useEffect(() => {
    if (market.data) {
      setDraft(fromMarket(market.data));
      const first = market.data.outcomes[0]?.key;
      if (first) setWinner(first);
    }
  }, [market.data]);

  useEffect(() => {
    if (!isNew || !config.data || draft.feeBps) return;
    setDraft((current) => ({
      ...current,
      feeBps: String(config.data?.feeBps ?? ''),
      minStake: santimToEtb(config.data?.minStake ?? 0),
      maxStake: santimToEtb(config.data?.maxStake ?? 0),
    }));
  }, [config.data, draft.feeBps, isNew]);

  const status = market.data?.status;
  const draftMode = isNew || status === 'draft';
  const openMode = status === 'open';

  function patch<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function save() {
    setBusy(true);
    try {
      if (isNew) {
        const created = await apiData<AdminMarketDto>('/admin/markets', {
          method: 'POST',
          body: payload(draft, true),
        });
        toastOk(t('saved'));
        await queryClient.invalidateQueries({ queryKey: ['markets'] });
        navigate(`/markets/${created.id}`);
      } else if (draftMode) {
        await apiData(`/admin/markets/${id}`, { method: 'PATCH', body: payload(draft, true) });
        toastOk(t('saved'));
        await market.refetch();
      } else if (openMode) {
        const sourceUrl = draft.sourceUrl.trim();
        const imageUrl = draft.imageUrl.trim();
        await apiData(`/admin/markets/${id}`, {
          method: 'PATCH',
          body: {
            description: { en: draft.descEn, am: draft.descAm },
            resolutionSource: {
              en: draft.sourceEn,
              am: draft.sourceAm,
              ...(sourceUrl ? { url: sourceUrl } : {}),
            },
            featured: draft.featured,
            ...(imageUrl ? { imageUrl } : {}),
            resolutionDate: fromAddisInput(draft.resolutionDate),
          },
        });
        toastOk(t('saved'));
        await market.refetch();
      }
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(false);
    }
  }

  async function call(path: string, body?: unknown) {
    setBusy(true);
    try {
      await apiData(`/admin/markets/${id}${path}`, { method: 'POST', body: body ?? {} });
      toastOk(t('saved'));
      await market.refetch();
      await queryClient.invalidateQueries({ queryKey: ['markets'] });
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(false);
    }
  }

  if (!isNew && market.isLoading) return <Spinner />;
  if (!isNew && (market.isError || !market.data)) {
    return <ErrorBlock message={t('errorTitle')} onRetry={() => void market.refetch()} />;
  }

  const locked = !draftMode && !openMode;

  return (
    <div>
      <PageHeader
        title={isNew ? t('markets.new') : format.text(market.data?.title)}
        subtitle={t('markets.editSubtitle')}
        actions={
          <Link to="/markets" className="text-sm font-semibold text-brand-text">
            {t('back')}
          </Link>
        }
      />
      {status ? (
        <div className="mb-4">
          <Badge tone={statusTone(status)}>{format.status(status)}</Badge>
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="space-y-3">
          <Field label={t('markets.titleEn')}>
            <TextInput
              data-testid="market-title-en"
              disabled={locked || openMode}
              value={draft.titleEn}
              onChange={(event) => patch('titleEn', event.target.value)}
            />
          </Field>
          <Field label={t('markets.titleAm')}>
            <TextInput
              data-testid="market-title-am"
              disabled={locked || openMode}
              value={draft.titleAm}
              onChange={(event) => patch('titleAm', event.target.value)}
            />
          </Field>
          <Field label={t('markets.descEn')}>
            <TextArea
              disabled={locked}
              value={draft.descEn}
              onChange={(event) => patch('descEn', event.target.value)}
            />
          </Field>
          <Field label={t('markets.descAm')}>
            <TextArea
              disabled={locked}
              value={draft.descAm}
              onChange={(event) => patch('descAm', event.target.value)}
            />
          </Field>
          <Field label={t('markets.category')}>
            <Select
              disabled={locked || openMode}
              value={draft.category}
              onChange={(event) => patch('category', event.target.value as Category)}
            >
              {CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {format.category(category)}
                </option>
              ))}
            </Select>
          </Field>
        </Card>
        <Card className="space-y-3">
          <Field label={t('markets.closes')} hint={t('markets.addisHint')}>
            <TextInput
              type="datetime-local"
              disabled={locked || openMode}
              value={draft.closesAt}
              onChange={(event) => patch('closesAt', event.target.value)}
            />
          </Field>
          <Field label={t('markets.resolves')} hint={t('markets.addisHint')}>
            <TextInput
              type="datetime-local"
              disabled={locked}
              value={draft.resolutionDate}
              onChange={(event) => patch('resolutionDate', event.target.value)}
            />
          </Field>
          <Field label={t('markets.sourceEn')}>
            <TextInput
              disabled={locked}
              value={draft.sourceEn}
              onChange={(event) => patch('sourceEn', event.target.value)}
            />
          </Field>
          <Field label={t('markets.sourceAm')}>
            <TextInput
              disabled={locked}
              value={draft.sourceAm}
              onChange={(event) => patch('sourceAm', event.target.value)}
            />
          </Field>
          <Field label={t('markets.sourceUrl')}>
            <TextInput
              disabled={locked}
              value={draft.sourceUrl}
              onChange={(event) => patch('sourceUrl', event.target.value)}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              disabled={locked}
              checked={draft.featured}
              onChange={(event) => patch('featured', event.target.checked)}
            />
            {t('markets.featured')}
          </label>
          {draftMode ? (
            <div className="grid grid-cols-3 gap-2">
              <Field label={t('markets.fee')}>
                <TextInput value={draft.feeBps} onChange={(event) => patch('feeBps', event.target.value)} />
              </Field>
              <Field label={t('markets.minStake')}>
                <TextInput value={draft.minStake} onChange={(event) => patch('minStake', event.target.value)} />
              </Field>
              <Field label={t('markets.maxStake')}>
                <TextInput value={draft.maxStake} onChange={(event) => patch('maxStake', event.target.value)} />
              </Field>
            </div>
          ) : null}
        </Card>
      </div>

      <Card className="mt-4">
        <h2 className="mb-3 font-semibold">{t('markets.outcomes')}</h2>
        <div className="space-y-3">
          {draft.outcomes.map((outcome, index) => (
            <div key={outcome.key} className="grid gap-2 sm:grid-cols-[8rem_1fr_1fr_auto]">
              <TextInput
                aria-label={t('markets.outcomeKey')}
                disabled={!draftMode}
                value={outcome.key}
                onChange={(event) => {
                  const outcomes = draft.outcomes.slice();
                  const current = outcomes[index];
                  if (!current) return;
                  outcomes[index] = { ...current, key: event.target.value };
                  patch('outcomes', outcomes);
                }}
              />
              <TextInput
                aria-label={t('markets.titleEn')}
                disabled={!draftMode}
                value={outcome.en}
                onChange={(event) => {
                  const outcomes = draft.outcomes.slice();
                  const current = outcomes[index];
                  if (!current) return;
                  outcomes[index] = { ...current, en: event.target.value };
                  patch('outcomes', outcomes);
                }}
              />
              <TextInput
                aria-label={t('markets.titleAm')}
                disabled={!draftMode}
                value={outcome.am}
                onChange={(event) => {
                  const outcomes = draft.outcomes.slice();
                  const current = outcomes[index];
                  if (!current) return;
                  outcomes[index] = { ...current, am: event.target.value };
                  patch('outcomes', outcomes);
                }}
              />
              {draftMode && draft.outcomes.length > 2 ? (
                <Button
                  variant="ghost"
                  onClick={() => patch('outcomes', draft.outcomes.filter((_, i) => i !== index))}
                >
                  {t('markets.remove')}
                </Button>
              ) : null}
            </div>
          ))}
        </div>
        {draftMode && draft.outcomes.length < 6 ? (
          <Button
            className="mt-3"
            variant="secondary"
            onClick={() =>
              patch('outcomes', [
                ...draft.outcomes,
                { key: `o${draft.outcomes.length + 1}`, en: '', am: '' },
              ])
            }
          >
            {t('markets.addOutcome')}
          </Button>
        ) : null}
        {market.data ? (
          <ul className="mt-4 space-y-1 text-sm text-fg-muted">
            {market.data.outcomes.map((outcome) => (
              <li key={outcome.key}>
                {format.text(outcome.title)} · {format.etb(outcome.pool)}
              </li>
            ))}
          </ul>
        ) : null}
      </Card>

      {market.data?.settlement ? (
        <Card className="mt-4">
          <h2 className="font-semibold">{t('markets.settlement')}</h2>
          <p className="mt-2 text-sm text-fg-muted">
            {t('markets.settlementLine', {
              pool: format.etb(market.data.settlement.totalPool),
              fee: format.etb(market.data.settlement.fee),
              paid: format.etb(market.data.settlement.totalPaidOut),
              dust: format.etb(market.data.settlement.dust),
            })}
          </p>
        </Card>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        {draftMode || openMode ? (
          <Button data-testid="market-save" loading={busy} onClick={() => void save()}>
            {t('save')}
          </Button>
        ) : null}
        {status === 'draft' ? (
          <Button data-testid="market-publish" variant="secondary" loading={busy} onClick={() => void call('/publish')}>
            {t('markets.publish')}
          </Button>
        ) : null}
        {status === 'open' ? (
          <Button data-testid="market-lock" variant="secondary" loading={busy} onClick={() => void call('/lock')}>
            {t('markets.lock')}
          </Button>
        ) : null}
        {status === 'draft' ? (
          <Button
            variant="danger"
            loading={busy}
            onClick={() => {
              setBusy(true);
              void apiData(`/admin/markets/${id}`, { method: 'DELETE' })
                .then(async () => {
                  toastOk(t('markets.deleted'));
                  await queryClient.invalidateQueries({ queryKey: ['markets'] });
                  navigate('/markets');
                })
                .catch((error) => toastError(error, t('errorTitle')))
                .finally(() => setBusy(false));
            }}
          >
            {t('markets.delete')}
          </Button>
        ) : null}
      </div>

      {status === 'open' || status === 'locked' ? (
        <div className="mt-3">
          <Button variant="danger" onClick={() => setVoidOpen(true)}>
            {t('markets.void')}
          </Button>
        </div>
      ) : null}

      {status === 'locked' ? (
        <Card className="mt-4 space-y-3">
          <h2 className="font-semibold">{t('markets.resolveTitle')}</h2>
          {market.data?.pendingResolution ? (
            <p className="text-sm">
              {t('markets.proposed', { outcome: market.data.pendingResolution.outcomeKey })}
            </p>
          ) : (
            <>
              <Field label={t('markets.winner')}>
                <Select value={winner} onChange={(event) => setWinner(event.target.value)}>
                  {draft.outcomes.map((outcome) => (
                    <option key={outcome.key} value={outcome.key}>
                      {outcome.en || outcome.key}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('markets.evidence')}>
                <TextArea
                  data-testid="market-evidence"
                  value={evidence}
                  onChange={(event) => setEvidence(event.target.value)}
                />
              </Field>
              <Button
                data-testid="market-propose"
                variant="secondary"
                loading={busy}
                disabled={evidence.trim().length < 3}
                onClick={() =>
                  void call('/propose-resolution', { outcomeKey: winner, evidenceNote: evidence.trim() })
                }
              >
                {t('markets.propose')}
              </Button>
            </>
          )}
          {market.data?.pendingResolution ? (
            <div className="flex gap-2">
              <Button data-testid="market-confirm" loading={busy} onClick={() => void call('/confirm-resolution')}>
                {t('markets.confirm')}
              </Button>
              <Button
                variant="danger"
                loading={busy}
                onClick={() => void call('/reject-resolution', { reason: t('markets.rejectDefault') })}
              >
                {t('markets.rejectProposal')}
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {voidOpen ? (
        <ConfirmDialog
          title={t('markets.void')}
          body={t('markets.voidBody')}
          phrase="VOID"
          confirmLabel={t('markets.void')}
          danger
          busy={busy}
          onClose={() => setVoidOpen(false)}
          onConfirm={() => {
            if (voidReason.trim().length < 3) return;
            setVoidOpen(false);
            void call('/void', { reason: voidReason.trim() });
          }}
        >
          <div className="mt-4">
            <Field label={t('reason')}>
              <TextInput value={voidReason} onChange={(event) => setVoidReason(event.target.value)} />
            </Field>
          </div>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
